import { IRawStyle, SelectableOptionMenuItemType } from '@fluentui/react';
import {
  ButtonProps,
  Dropdown,
  Field,
  InfoLabel,
  Option,
  OptionGroup,
  OptionOnSelectData,
  SelectionEvents,
  SlotComponentType,
} from '@fluentui/react-components';
import { Dropdown as DropdownV8 } from '@fluentui/react/lib/Dropdown'; //remove this after migration of this component
import { makeStyles, mergeClasses } from '@griffel/react';
import { DesignSystem, useAppTheme } from 'App/AppDesignSystem';
import { IPText } from 'baseComponents';
import { IPSelectableOption, IPSelectableOptionGroup } from 'baseComponents/Utils/SelectableOption';
import * as React from 'react';
import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';

export interface IPDropdownProps {
  label?: string;
  info?: string;
  id?: string;
  className?: string;
  selectedKey?: string | number | null;
  selectedKeys?: string[] | number[] | null;
  multiSelect?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  placeholder?: string;
  options: IPSelectableOption[]; //do not support options groups. Remove it from implementation
  onChange?: (event: React.FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => void;
  title?: string;
  prefix?: string;
  styles?: IPDropdownCustomStyles;
  onClick?: ((e: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined;
}

export interface IPDropdownCustomStyles {
  root?: CSSProperties;
  dropdown?: CSSProperties;
  listbox?: CSSProperties;
}

const useFluentStyles = makeStyles({
  dropdown: {
    minWidth: '0px',
    width: '100%',
    paddingBottom: '0.5px',
  },
  root: {
    paddingTop: '2.5px',
    fontWeight: '600',
  },
});

/** Flatten grouped options once; keep outside component to avoid re-allocating helpers */
function flattenOptions(opts: IPSelectableOption[] | IPSelectableOptionGroup[] | undefined): IPSelectableOption[] {
  if (!opts || opts.length === 0) return [];
  const out: IPSelectableOption[] = [];
  const add = (list: IPSelectableOption[] | IPSelectableOptionGroup[]) => {
    list.forEach((o) => {
      if ((o as IPSelectableOptionGroup).options !== undefined) {
        add((o as IPSelectableOptionGroup).options);
      } else {
        const sel = o as IPSelectableOption;
        out.push({ ...sel, key: sel.key });
      }
    });
  };
  add(opts);

  return out;
}

export const IPDropdown = (props: IPDropdownProps) => {
  const { system } = useAppTheme(); // Get the current design system
  const stylesFluent = useFluentStyles();
  const flatOptions = useMemo(() => flattenOptions(props.options), [props.options]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [value, setValue] = useState<string>('');

  useEffect(() => {
    const { selectedKey, selectedKeys } = props;
    let selectedFromProps: string[] | undefined;

    if (selectedKeys && selectedKeys.length > 0) {
      selectedFromProps = selectedKeys.map((k) => k.toString());
    } else if (selectedKeys) {
      selectedFromProps = [];
    } else if (selectedKey || selectedKey === 0) {
      selectedFromProps = [String(selectedKey)];
    }

    if (selectedFromProps) {
      setSelectedOptions(selectedFromProps);
      const first = flatOptions.find((o) => selectedFromProps!.includes(String(o.key)));
      setValue(first?.text?.toString() ?? '');
    }
  }, [props.selectedKey, props.selectedKeys, flatOptions, props]);

  const renderOptions = useCallback((opts: IPSelectableOption[] | IPSelectableOptionGroup[]) => {
    if (!opts || opts.length === 0) return null;
    const isGrouped = (opts[0] as IPSelectableOptionGroup).options !== undefined;

    if (!isGrouped) {
      return (opts as IPSelectableOption[]).map((option) => (
        <Option value={String(option.key)} text={option.text ?? ''} title={option.title} key={option.key}>
          {option?.text ?? ''}
        </Option>
      ));
    }

    return (opts as IPSelectableOptionGroup[]).map((group, idx) => (
      <OptionGroup label={group.groupLabel} key={`${group.groupLabel ?? 'group'}-${idx}`}>
        {group.options.map((option) => (
          <Option value={String(option.key)} key={option.key} text={option.text ?? ''} title={option.title}>
            {option?.text ?? ''}
          </Option>
        ))}
      </OptionGroup>
    ));
  }, []);

  const getLabelSlot = useCallback(() => {
    const { info: about, label } = props;
    if (!about) return label;

    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children: (_: unknown, slotProps: any) => (
        <InfoLabel {...slotProps} info={about}>
          {label}
        </InfoLabel>
      ),
    };
  }, [props]);

  const { onChange, className, styles, multiSelect, title, prefix, errorMessage } = props;

  const fluentOnOptionSelect = useCallback(
    (event: SelectionEvents, data?: OptionOnSelectData) => {
      if (!onChange || !data?.optionValue) return;
      const formEvent = event as unknown as React.FormEvent<HTMLDivElement>;

      const idx = flatOptions.findIndex((o) => String(o.key) === data.optionValue);
      if (idx === -1) return;
      const found = flatOptions[idx];

      const selectableOption: IPSelectableOption = {
        ...found,
        selected: data.selectedOptions?.includes(String(found.key)) ?? false,
      };

      if (multiSelect) {
        setSelectedOptions(data.selectedOptions ?? []);
      } else {
        setSelectedOptions([String(found.key)]);
        setValue(found.text ?? '');
      }

      onChange(formEvent, selectableOption, idx);
    },
    [onChange, flatOptions, multiSelect],
  );

  // Memoize the custom button children transformer so it doesn't re-create each render
  const buttonChildren = useCallback(
    (Component: SlotComponentType<ButtonProps>, slotProps: ButtonProps) => {
      // Only run this if title/prefix is provided; otherwise we don't pass `button` prop at all
      const originalChildren = slotProps.children;
      if (!originalChildren?.props?.children) return <Component {...slotProps} />;
      const { props: childProps, ...childrenWithoutProps } = originalChildren;
      const { children: childPropsChildren, ...childPropsWithoutChildren } = childProps;

      const newChildPropsChildren = [(prefix ?? '') + (title ?? childPropsChildren[0]), childPropsChildren[1]];

      const newChildProps = { ...childPropsWithoutChildren, children: newChildPropsChildren };
      const newChildren = { ...childrenWithoutProps, props: newChildProps };

      return <Component {...slotProps}>{newChildren}</Component>;
    },
    [prefix, title],
  );

  //
  // Main render
  //
  if (system === DesignSystem.FluentV9) {
    const hasError = Boolean(errorMessage);

    return (
      <Field
        label={getLabelSlot()}
        className={stylesFluent.root}
        style={props.styles?.root}
        validationState={hasError ? 'error' : undefined}
        validationMessage={errorMessage}
      >
        <Dropdown
          id={props.id}
          button={title || prefix ? { children: buttonChildren } : undefined}
          className={mergeClasses(stylesFluent.dropdown, className)}
          listbox={{ style: props.styles?.listbox }}
          style={styles?.dropdown}
          selectedOptions={selectedOptions}
          multiselect={multiSelect}
          disabled={props.disabled}
          value={value}
          onOptionSelect={fluentOnOptionSelect}
          placeholder={props.placeholder}
          size="medium"
          positioning={{ autoSize: 'width' }}
          aria-invalid={hasError ? true : undefined}
          aria-describedby={hasError ? `${props.id ?? 'ipdropdown'}-error` : undefined}
        >
          {renderOptions(props.options)}
        </Dropdown>
      </Field>
    );
  } else {
    //remove this after migration of this component
    const mapOptions = (options: IPSelectableOption[]) => {
      return options.map((opt) => {
        // Map itemType if present and ensure it's only SelectableOptionMenuItemType or undefined
        let mappedItemType: SelectableOptionMenuItemType | undefined = undefined;
        if (opt.itemType !== undefined && SelectableOptionMenuItemType) {
          // If itemType is a string or number, map it to the enum value if possible
          if (
            typeof opt.itemType === 'string' &&
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (SelectableOptionMenuItemType as any)[opt.itemType] !== undefined
          ) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            mappedItemType = (SelectableOptionMenuItemType as any)[opt.itemType];
          } else if (
            typeof opt.itemType === 'number' &&
            Object.values(SelectableOptionMenuItemType).includes(
              opt.itemType as unknown as SelectableOptionMenuItemType,
            )
          ) {
            mappedItemType = opt.itemType as unknown as SelectableOptionMenuItemType;
          }
        }

        return {
          ...opt,
          itemType: mappedItemType,
        };
      });
    };

    // Adapt onChange to match DropdownV8's expected signature and option type
    const onChangeV8 = props.onChange
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (event: React.FormEvent<HTMLDivElement>, option?: any, index?: number) => {
          // option is IDropdownOption, but our handler expects IPSelectableOption
          props.onChange?.(event, option as IPSelectableOption, index);
        }
      : undefined;

    // Omit the 'styles' prop when passing to DropdownV8 to avoid type incompatibility
    const propsWithoutStyles: Omit<IPDropdownProps, 'styles'> = { ...props };

    // Convert CSSProperties to IRawStyle for CheckboxV8
    const convertToIRawStyle = (style?: CSSProperties): IRawStyle | undefined => {
      if (!style) return undefined;
      // If already IRawStyle, return as is
      if (typeof style === 'object' && !('MozOsxFontSmoothing' in style)) {
        return style as IRawStyle;
      }

      // Otherwise, shallow copy CSSProperties as IRawStyle
      return { ...(style as IRawStyle) };
    };

    const v8Styles = props.styles
      ? {
          root: convertToIRawStyle(props.styles.root),
          dropdown: convertToIRawStyle(props.styles.dropdown),
          listbox: convertToIRawStyle(props.styles.listbox),
        }
      : undefined;

    return (
      <DropdownV8
        {...propsWithoutStyles}
        onChange={onChangeV8}
        options={mapOptions(props.options as IPSelectableOption[])}
        dropdownWidth={'auto'}
        onRenderTitle={props.title ? () => <IPText>{props.title}</IPText> : undefined}
        styles={v8Styles}
      />
    );
  }
};
